/*global $:false */

import { decodeEscapes, encodeEscapes } from '../fields/recordm.fields.js';

import BaseM from './recordm.model.entity.js';
import Create from '../fields/recordm.fields.create.js';
import ErrorType from '../helpers/error-type.js';

import { getIndexFieldName } from "../helpers/recordm.value-formatter";
import { sprintf } from 'sprintf-js';

import ui from '@cob/ui-genesis/cob.ui.js';

// Model representing an indexed instance, i.e. an instance comming from ES.
// This is different from the normal Instance because indexed data is in a different representation.
//
// This is used in the unified search and in the definition search. (jone: só?)
var IndexedInstanceM = function(core, data) {
    /* eslint-disable-next-line prefer-rest-params */
    BaseM.apply(this, arguments);

    this.data = data || {};
    this._core = core;

    this._entityKey = IndexedInstanceM.entityKey;

    this.loaded_event_name = "recordm:loaded:instances/indexed:" + this.data.id;
    this.update_event_name = "recordm:saved:instances/indexed:" + this.data.id;
    this.delete_event_name = "recordm:deleted:instances/indexed:" + this.data.id;

    //Array of the fields description {name: <field name>, id: <field def id>, desc: <the field description>}
    //used for operations that require the knowledge of a field's keywords
    this.fieldDescriptions=[];
};

IndexedInstanceM.entityKey = 'instances/indexed';
IndexedInstanceM.prototype = new BaseM();
IndexedInstanceM.prototype.constructor = IndexedInstanceM;

IndexedInstanceM.get = BaseM.buildGetFor(IndexedInstanceM);

// Return the instance id associated to this IndexedInstanceM
IndexedInstanceM.prototype.getInstanceId = function() {
    return this.data.id;
};

// Returns the instance label of this instance
// This is usefull to update the breadcrumb information
IndexedInstanceM.prototype.getDescription = function() {
    return this.getInstanceLabel();
};

// Returns the instance main identifier.
// Its the first line in the unified result.
//
// Note: It only shows the first field with the key $instanceLabel
IndexedInstanceM.prototype.getInstanceLabel = function(formatValue=false, locale) {
    if (this.data._definitionInfo.instanceLabel && this.data._definitionInfo.instanceLabel.length > 0) {
        // Just return the first
        const field = this.data._definitionInfo.instanceLabel[0],
            indexField = getIndexFieldName(field.name),
            value = (formatValue
                     ? BaseM.getReadableFieldValue(this.data, indexField, field.description, field.id, locale)
                     : BaseM.getReadableFieldValue(this.data, indexField));

        return value ?? String(this.data.id);

    } else {
        return String(this.data.id);
    }
};

// Returns an array with a field name and field value.
// it should appear under the main identifier in the unified search result
IndexedInstanceM.prototype.getInstanceDescription = function(locale) {
    const _this = this;
    const descriptors = [
        {
            label: this._core.translateString("recordm.search", "search.definition"),
            value: this.data._definitionInfo.name
        }
    ];

    if (this.data._definitionInfo.instanceDescription) {
        this.data._definitionInfo.instanceDescription.forEach(function(field) {
            const indexField = getIndexFieldName(field.name),
                value = BaseM.getReadableFieldValue(_this.data, indexField, field.description, field.id, locale);

            descriptors.push({
                label: field.name,
                value: value,
                description: field.description
            })
        })
    }

    return descriptors;
};

// Update this instance's `references` property with the number of instances that
// have $ref fields pointing to it. It is then used for the confirmation msg
// if the user tries to delete this instance.
//
// args:
//  * `onDone` : After getting a successfull response
//  * `onError` : After getting an error response
IndexedInstanceM.prototype.updateTotalReferencing = function(onDone, onError) {
    var _this = this;

    $.ajax({
        'url': 'recordm/instances/' + _this.data.id + "/countrefs",
        'type': 'get',
        success: function(result) {
            _this.data.references = result.references;

            if (onDone) onDone();
        },
        error: function() {
            ui.notification.showCustom(
                'error',
                sprintf(_this._core.translateString("recordm.instance", "msg.error-title"), _this.data.id),
                _this._core.translateString("recordm.instance", "msg.not-found")
            );

            if (onError) onError();
        }
    });
};

// returns true if the user is allowed to update the instance
// associated to this indexed instance object
IndexedInstanceM.prototype.canUpdate = function(){
    return this.data._links && this.data._links.update ;
};

// returns true if the user is allowed to delete the instance
// associated to this indexed instance object
IndexedInstanceM.prototype.canDelete = function(){
    return this.data._links && this.data._links['delete'];
};

// Delete the instance associated to this IndexedInstanceM
IndexedInstanceM.prototype.deleteSelf = function(opts) {
    var _this = this,
        _opts = opts || {},
        _endpointPath = _this.data._links['delete'];

    if (_this.data.references) {
        _endpointPath += "?ignoreRefs=true";
    }

    $.ajax({
        url: _endpointPath,
        type: "delete",
        success: function() {
            _this._core.publish(_this.delete_event_name, {entityKey: _this._entityKey, id: _this.data.id});

            _this.release();

            if (_opts.success) opts.success(_this.data.id);
        },
        error: function(req) {
            window.console.log("ENTITY:" + _this._entityKey + ":error-delete: " +
                               req.status + " : " + req.statusText + ":" + req.responseText);

            if (_opts.error) {
                if (req.status === 409) {
                    var reasons = JSON.parse(req.responseText).map(function(item) {
                        return ErrorType[item.errorType];
                    });

                    _opts.error(_this._core.translateString("recordm.error.codes", "error.delete.deleting"), reasons);

                } else if (req.status === 404) {
                    _opts.error(_this._core.translateString("recordm.error.codes", "error.delete.not-found"));

                } else {
                    _opts.error(_this._core.translateString("recordm.error.codes", "error.delete.unknown"));
                }
            }
        }
    });
};

IndexedInstanceM.prototype.getFullData = function() {
    return this.data;
};

export default IndexedInstanceM;
